import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";
import MerchantDashboard from "@/pages/merchant/MerchantDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import JoinCardPage from "@/pages/JoinCardPage";
import MentionsLegales from "@/pages/legal/MentionsLegales";
import PolitiqueConfidentialite from "@/pages/legal/PolitiqueConfidentialite";
import CguClients from "@/pages/legal/CguClients";
import CguProfessionnels from "@/pages/legal/CguProfessionnels";
import { Toaster } from "@/components/ui/sonner";
import { useEffect, useState, createContext, useContext } from "react";

import DiagnosticPage from "@/components/DiagnosticPage";

// PWA Install context
const PwaContext = createContext(null);
export const usePwa = () => useContext(PwaContext);

function ProtectedRoute({ children, role, adminOnly }) {
    const { user, loading } = useAuth();
    if (loading) return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" />
        </div>
    );
    if (!user) return <Navigate to="/connexion" />;
    if (adminOnly && !user.is_admin) return <Navigate to="/" />;
    if (role && user.role !== role && !user.is_admin) return <Navigate to="/" />;
    return children;
}

function getHomeRoute(user) {
    if (!user) return "/";
    if (user.is_admin) return "/admin";
    if (user.role === "merchant") return "/commercant";
    return "/client";
}

function AppRoutes() {
    const { user } = useAuth();
    const home = getHomeRoute(user);

    return (
        <Routes>
            <Route path="/" element={user ? <Navigate to={home} /> : <LandingPage />} />
            <Route path="/connexion" element={user ? <Navigate to={home} /> : <LoginPage />} />
            <Route path="/inscription" element={user ? <Navigate to={home} /> : <RegisterPage />} />
            <Route path="/client" element={<ProtectedRoute role="client"><CustomerDashboard /></ProtectedRoute>} />
            <Route path="/commercant" element={<ProtectedRoute role="merchant"><MerchantDashboard /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
            <Route path="/rejoindre/:cardId" element={<JoinCardPage />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/confidentialite" element={<PolitiqueConfidentialite />} />
            <Route path="/cgu-clients" element={<CguClients />} />
            <Route path="/cgu-professionnels" element={<CguProfessionnels />} />
            <Route path="/diagnostic" element={<DiagnosticPage />} />
        </Routes>
    );
}

function OneSignalInit() {
    const { user } = useAuth();

    // Load and initialize OneSignal SDK
    useEffect(() => {
        if (window.__onesignal_loading) return;
        window.__onesignal_loading = true;
        window.__onesignal_ready = false;
        window.__onesignal_error = null;

        const script = document.createElement('script');
        script.src = '/OneSignalSDK.page.js?v=' + Date.now();
        script.defer = true;
        script.onerror = () => {
            window.__onesignal_error = 'SDK non charge';
        };
        script.onload = () => {
            window.OneSignalDeferred = window.OneSignalDeferred || [];
            window.OneSignalDeferred.push(async function(OneSignal) {
                try {
                    await OneSignal.init({
                        appId: "716aa58f-2072-4a62-b2af-99bd34dc237e",
                        allowLocalhostAsSecureOrigin: true,
                        serviceWorkerParam: { scope: "/" },
                        serviceWorkerPath: "/OneSignalSDKWorker.js",
                    });
                    window.__onesignal_ready = true;
                    console.log("[OneSignal] SDK initialized, permission:", OneSignal.Notifications.permission, "optedIn:", OneSignal.User.PushSubscription.optedIn);
                } catch(e) {
                    window.__onesignal_error = e.message || 'Init failed';
                    console.error("[OneSignal] Init error:", e);
                }
            });
        };
        document.head.appendChild(script);
    }, []);

    // Tag user when logged in
    useEffect(() => {
        if (!user) return;
        const tagAndResubscribe = async () => {
            // Wait for SDK to be ready (up to 20s)
            for (let i = 0; i < 40; i++) {
                if (window.__onesignal_ready) break;
                if (window.__onesignal_error) return;
                await new Promise(r => setTimeout(r, 500));
            }
            try {
                const OS = window.OneSignal;
                if (!OS || !window.__onesignal_ready) return;

                // Login and set tags
                await OS.login(user.id);

                // Auto re-subscribe if permission was previously granted but subscription is missing
                if ('Notification' in window && Notification.permission === 'granted') {
                    const isOptedIn = OS.User.PushSubscription.optedIn;
                    if (!isOptedIn) {
                        console.log('[OneSignal] Permission granted but not opted in - re-subscribing...');
                        try {
                            await OS.User.PushSubscription.optIn();
                            await new Promise(r => setTimeout(r, 2000));
                            console.log('[OneSignal] Re-subscribed, optedIn:', OS.User.PushSubscription.optedIn);
                        } catch (optErr) {
                            console.log('[OneSignal] OptIn failed, trying manual SW registration...');
                        }

                        // If still not opted in, try manual service worker + push subscription
                        if (!OS.User.PushSubscription.optedIn && 'serviceWorker' in navigator) {
                            try {
                                let swReg = await navigator.serviceWorker.getRegistration('/');
                                if (!swReg) {
                                    swReg = await navigator.serviceWorker.register('/OneSignalSDKWorker.js', { scope: '/' });
                                    await navigator.serviceWorker.ready;
                                }
                                // Try optIn again after ensuring SW is registered
                                await OS.User.PushSubscription.optIn();
                                await new Promise(r => setTimeout(r, 2000));
                                console.log('[OneSignal] After manual SW, optedIn:', OS.User.PushSubscription.optedIn);
                            } catch (swErr) {
                                console.log('[OneSignal] Manual SW registration failed:', swErr);
                            }
                        }
                    }
                }
            } catch (e) {
                console.log('[OneSignal] tag/resubscribe error:', e);
            }
        };
        const timer = setTimeout(tagAndResubscribe, 2000);
        return () => clearTimeout(timer);
    }, [user]);

    return null;
}

function App() {
    const [installPrompt, setInstallPrompt] = useState(null);

    useEffect(() => {
        const handler = (e) => {
            e.preventDefault();
            setInstallPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    return (
        <PwaContext.Provider value={{ installPrompt, setInstallPrompt }}>
            <AuthProvider>
                <BrowserRouter>
                    <OneSignalInit />
                    <AppRoutes />
                    <Toaster richColors position="top-right" />
                </BrowserRouter>
            </AuthProvider>
        </PwaContext.Provider>
    );
}

export default App;
